import React, { useContext, useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { DContext } from '../../../context/Datacontext'
import { fetchCourseDetail } from '../../../services/students/courses/fetchCourseDetail'
import { createOrder } from '../../../services/students/courses/createOrder'
import { saveWatchProgress, getWatchProgress, getCourseProgress, markAsCompleted } from '../../../services/students/courses/watchProgress'
import { submitCourseFeedback } from '../../../services/students/courses/submitFeedback'
import { startConversation } from '../../../services/user/messagingService'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import {
    FiBook,
    FiClock,
    FiGlobe,
    FiUsers,
    FiCheckCircle,
    FiLock,
    FiPlay,
    FiAlertCircle,
    FiChevronDown,
    FiChevronUp,
    FiStar,
    FiMessageCircle
} from 'react-icons/fi'
// import { FaSignalMessenger } from "react-icons/fa6";
import { BiRupee } from 'react-icons/bi'
import { LuPartyPopper } from 'react-icons/lu'
import Loading from '../../Loading'
import { MdKeyboardArrowDown } from "react-icons/md";
import FeedbackSection from './FeedbackSection'
import FetchMessages from "../../../features/Messages";
import { TbMessage } from "react-icons/tb";
import { VideoDuration } from '../../../utils/VideoDuration'


function CourseView() {
    const { courseId } = useParams()
    const navigate = useNavigate()
    const { BeURL, currentUser } = useContext(DContext)

    const [course, setCourse] = useState(null)
    const [enrollmentStatus, setEnrollmentStatus] = useState({
        isEnrolled: false,
        hasAccess: false,
        accessExpired: false,
        orderId: null
    })
    const [watchProgress, setWatchProgress] = useState({})
    const [courseProgress, setCourseProgress] = useState({
        percentage: 0,
        countPercentage: 0,
        completedAssets: 0,
        totalAssets: 0,
        completedPoints: 0,
        totalPoints: 0,
        sessionProgress: {},
        lastUpdated: null
    })
    const [loading, setLoading] = useState(true)
    const [enrolling, setEnrolling] = useState(false)
    const [selectedSession, setSelectedSession] = useState(null)
    const [expandedSyllabus, setExpandedSyllabus] = useState(null)
    const [error, setError] = useState(null)
    const [showFeedbackForm, setShowFeedbackForm] = useState(false)
    const [feedbackData, setFeedbackData] = useState({ rating: 0, description: '' })
    const [submittingFeedback, setSubmittingFeedback] = useState(false)
    const [hasFeedback, setHasFeedback] = useState(false)
    const [messagingLoading, setMessagingLoading] = useState(false)
    const [videoError, setVideoError] = useState(false)
    const [showFullDesc, setShowFullDesc] = useState(false);
    const [openFAQ, setOpenFAQ] = useState(null)
    const [showMessager, setShowMessager] = useState(false)

    // Video player refs
    const videoRef = useRef(null)
    const playerRef = useRef(null)
    const progressIntervalRef = useRef(null)
    const courseProgressIntervalRef = useRef(null)
    const fetchCourseProgressRef = useRef(null)

    // Fetch course details
    useEffect(() => {
        const loadCourse = async () => {
            if (!courseId) {
                setError('Course ID is missing')
                setLoading(false)
                return
            }

            try {
                setLoading(true)
                const result = await fetchCourseDetail({ BeURL, courseId })

                if (!result.success) {
                    setError(result.message || 'Failed to load course')
                    setLoading(false)
                    return
                }

                setCourse(result.course)
                setEnrollmentStatus(result.enrollmentStatus || {
                    isEnrolled: false,
                    hasAccess: false,
                    accessExpired: false,
                    orderId: null
                })

                // Check if user has already submitted feedback
                if (currentUser?._id && result.course?.feedback) {
                    const userFeedback = result.course.feedback.find(
                        fb => fb.id === currentUser._id
                    )
                    setHasFeedback(!!userFeedback)
                }

                // Load watch progress if enrolled
                if (result.enrollmentStatus?.isEnrolled) {
                    const progressResult = await getWatchProgress({ BeURL, courseId })
                    if (progressResult.success) {
                        setWatchProgress(progressResult.progress || {})
                    }

                    // Load initial course progress
                    const courseProgressResult = await getCourseProgress({ BeURL, courseId })
                    if (courseProgressResult.success && courseProgressResult.progress) {
                        setCourseProgress(courseProgressResult.progress)
                    }
                }

                // Auto-select first video session
                if (result.course?.syllabuses?.length > 0) {
                    const firstSyllabus = result.course.syllabuses[0]
                    if (firstSyllabus.assets?.length > 0) {
                        const firstVideo = firstSyllabus.assets.find(a => a.type === 'video')
                        if (firstVideo) {
                            setSelectedSession({
                                ...firstVideo,
                                syllabusId: firstSyllabus.id
                            })
                            setExpandedSyllabus(firstSyllabus.id)
                        }
                    }
                }

                setLoading(false)
            } catch (err) {
                console.error('Error loading course:', err)
                setError('An error occurred while loading the course')
                setLoading(false)
            }
        }

        loadCourse()
    }, [courseId, BeURL, currentUser?._id])

    // Fetch course progress every 1 minute if enrolled
    useEffect(() => {
        if (!courseId || !BeURL || !enrollmentStatus.isEnrolled) {
            return
        }

        const fetchCourseProgress = async () => {
            try {
                const result = await getCourseProgress({ BeURL, courseId })
                if (result.success && result.progress) {
                    setCourseProgress(result.progress)

                    // Also refresh watch progress for individual sessions
                    const progressResult = await getWatchProgress({ BeURL, courseId })
                    if (progressResult.success) {
                        setWatchProgress(progressResult.progress || {})
                    }
                }
            } catch (err) {
                console.error('Error fetching course progress:', err)
            }
        }

        // Store function in ref for access from other useEffects
        fetchCourseProgressRef.current = fetchCourseProgress

        // Fetch immediately on mount
        fetchCourseProgress()

        // Set interval to fetch every 1 minute (60000 ms)
        courseProgressIntervalRef.current = setInterval(fetchCourseProgress, 60000)

        // Cleanup on unmount
        return () => {
            if (courseProgressIntervalRef.current) {
                clearInterval(courseProgressIntervalRef.current)
            }
        }
    }, [courseId, BeURL, enrollmentStatus.isEnrolled])

    // DevTools blocking (keyboard shortcuts and right-click only)
    useEffect(() => {
        // Block right-click on entire page
        const handleContextMenu = (e) => {
            e.preventDefault();
            return false;
        };

        // Block keyboard shortcuts
        const handleKeyDown = (e) => {
            // F12
            if (e.key === 'F12') {
                e.preventDefault();
                alert('Developer tools are not allowed on this page.');
                return false;
            }
            // Ctrl+Shift+I or Cmd+Option+I (Inspect)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
                e.preventDefault();
                alert('Developer tools are not allowed on this page.');
                return false;
            }
            // Ctrl+Shift+J or Cmd+Option+J (Console)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'J' || e.key === 'j')) {
                e.preventDefault();
                alert('Developer tools are not allowed on this page.');
                return false;
            }
            // Ctrl+Shift+C (Inspect Element)
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'C' || e.key === 'c')) {
                e.preventDefault();
                alert('Developer tools are not allowed on this page.');
                return false;
            }
            // Ctrl+U or Cmd+U (View Source)
            if ((e.ctrlKey || e.metaKey) && (e.key === 'u' || e.key === 'U')) {
                e.preventDefault();
                return false;
            }
        };

        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    // Video player initialization and management
    useEffect(() => {
        if (!selectedSession || !course || selectedSession.type !== 'video') return
        const videoSrc = `${BeURL}/course/${course._id}/syllabus/${selectedSession.syllabusId}/session/${selectedSession._id}/stream`
        // Reset video error state when loading new video
        setVideoError(false)
        // Check if player exists but is in error state or disposed
        if (playerRef.current) {
            try {
                // Check if player is still valid
                const isPlayerDisposed = !playerRef.current.el_ || playerRef.current.el_.parentNode === null
                if (isPlayerDisposed) {
                    playerRef.current = null
                }
            } catch (err) {
                console.log('Player in invalid state, reinitializing...')
                playerRef.current = null
            }
        }

        // Initialize or update player
        if (!playerRef.current && videoRef.current) {
            console.log('Initializing new video player...')
            playerRef.current = videojs(videoRef.current, {
                controls: true,
                fluid: true,
                preload: 'auto',
                controlBar: {
                    downloadButton: false, // Disable download button
                    pictureInPictureToggle: false // Disable PIP
                },
                sources: [{
                    src: videoSrc,
                    type: selectedSession.file?.mimeType || 'video/mp4'
                }]
            })

            // Security: Disable right-click on video
            const videoElement = playerRef.current.el();
            videoElement.addEventListener('contextmenu', (e) => {
                e.preventDefault();
                return false;
            });

            // Security: Disable video download attribute
            const videoTag = videoElement.querySelector('video');
            if (videoTag) {
                videoTag.removeAttribute('controlsList');
                videoTag.setAttribute('controlsList', 'nodownload');
                videoTag.disablePictureInPicture = true;
                videoTag.removeAttribute('download');

                // Prevent drag and drop
                videoTag.addEventListener('dragstart', (e) => {
                    e.preventDefault();
                    return false;
                });
            }

            // Set up time tracking
            playerRef.current.on('timeupdate', () => {
                const currentTime = playerRef.current.currentTime()
                // const duration = playerRef.current.duration()

                // Check if preview only
                if (!enrollmentStatus.hasAccess && currentTime >= 300) { // 5 minutes
                    playerRef.current.pause()
                    alert('Preview limit reached (5 minutes). Please enroll to watch the full course.')
                }
            })

            // Handle video errors with recovery
            playerRef.current.on('error', () => {
                const error = playerRef.current.error()
                console.error('Video player error:', error)

                if (error && error.code === 4) {
                    // Media source not supported or access denied
                    if (!enrollmentStatus.hasAccess) {
                        alert('This content is not available for preview. Please enroll to access all course materials.')
                        setVideoError(true)
                    } else {
                        console.log('Video load error, attempting to recover...')
                        // Attempt to recover by reloading the source
                        setTimeout(() => {
                            if (playerRef.current) {
                                try {
                                    playerRef.current.src({
                                        src: videoSrc,
                                        type: selectedSession.file?.mimeType || 'video/mp4'
                                    })
                                    playerRef.current.load()
                                } catch (err) {
                                    console.error('Failed to recover video:', err)
                                    setVideoError(true) // Show reload button if auto-recovery fails
                                }
                            } else {
                                setVideoError(true)
                            }
                        }, 1000)
                    }
                } else if (error) {
                    console.error('Video error code:', error.code, 'message:', error.message)
                    setVideoError(true)
                }
            })

            // Handle video loaded event
            playerRef.current.on('loadeddata', () => {
                console.log('Video loaded successfully')
            })

            // Handle buffer events
            playerRef.current.on('waiting', () => {
                console.log('Video buffering...')
            })

            playerRef.current.on('canplay', () => {
                console.log('Video ready to play')
            })

            // Security: Pause video when tab is not visible
            const handleVisibilityChange = () => {
                if (document.hidden && playerRef.current && !playerRef.current.paused()) {
                    playerRef.current.pause();
                }
            };
            document.addEventListener('visibilitychange', handleVisibilityChange);

            // Security: Prevent keyboard shortcuts for video download
            const handleKeyDown = (e) => {
                // Prevent Ctrl+S (save), Ctrl+Shift+S (save as)
                if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
                    e.preventDefault();
                    return false;
                }
                // Prevent F12, Ctrl+Shift+I (DevTools)
                if (e.key === 'F12' ||
                    ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i'))) {
                    e.preventDefault();
                    return false;
                }
            };
            videoElement.addEventListener('keydown', handleKeyDown);

            // Store cleanup functions
            videoElement._cleanupListeners = () => {
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                videoElement.removeEventListener('keydown', handleKeyDown);
            };

            // Resume from saved progress
            const savedProgress = watchProgress[selectedSession._id]
            if (savedProgress?.time && enrollmentStatus.hasAccess) {
                playerRef.current.currentTime(savedProgress.time)
            }

        } else if (playerRef.current) {
            try {
                // Update source
                playerRef.current.pause()

                // Clear any existing error state
                playerRef.current.error(null)

                playerRef.current.src({
                    src: videoSrc,
                    type: selectedSession.file?.mimeType || 'video/mp4'
                })
                playerRef.current.load()

                // Wait for video to be ready before seeking
                const handleLoadedMetadata = () => {
                    const savedProgress = watchProgress[selectedSession._id]
                    if (savedProgress?.time && enrollmentStatus.hasAccess) {
                        playerRef.current.currentTime(savedProgress.time)
                    } else {
                        playerRef.current.currentTime(0)
                    }
                    playerRef.current.off('loadedmetadata', handleLoadedMetadata)
                }

                playerRef.current.on('loadedmetadata', handleLoadedMetadata)

                // Don't autoplay - let user start the video manually
                // This prevents browser autoplay prevention errors
            } catch (err) {
                console.error('Error updating video source:', err)
                // If update fails, dispose and reinitialize on next effect run
                if (playerRef.current) {
                    try {
                        playerRef.current.dispose()
                    } catch (disposeErr) {
                        console.error('Error disposing player:', disposeErr)
                    }
                    playerRef.current = null
                }
            }
        }

        // Save progress periodically (every 5 seconds)
        if (enrollmentStatus.hasAccess) {
            progressIntervalRef.current = setInterval(async () => {
                if (playerRef.current) {
                    try {
                        // Check if player is still valid before accessing
                        if (!playerRef.current.el_ || playerRef.current.isDisposed()) {
                            console.log('Player disposed, clearing interval')
                            clearInterval(progressIntervalRef.current)
                            return
                        }

                        const currentTime = playerRef.current.currentTime()
                        const duration = playerRef.current.duration()
                        const completed = duration > 0 && currentTime / duration > 0.9

                        // Only save if we have valid time data
                        if (!isNaN(currentTime) && !isNaN(duration) && currentTime > 0) {
                            const result = await saveWatchProgress({
                                BeURL,
                                sessionId: selectedSession._id,
                                time: currentTime,
                                completed,
                                courseId  // Send courseId for better progress tracking
                            })

                            // If video was marked as completed, immediately refresh progress to update UI
                            if (result.success && completed && fetchCourseProgressRef.current) {
                                console.log('Video completed, refreshing progress...')
                                await fetchCourseProgressRef.current()
                            }
                        }
                    } catch (err) {
                        console.error('Error saving progress:', err)
                    }
                }
            }, 5000)
        }

        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current)
                progressIntervalRef.current = null
            }
            // Cleanup security listeners
            if (playerRef.current && playerRef.current.el()?._cleanupListeners) {
                try {
                    playerRef.current.el()._cleanupListeners()
                } catch (err) {
                    console.error('Error cleaning up listeners:', err)
                }
            }
        }
    }, [selectedSession, course, BeURL, enrollmentStatus.hasAccess, watchProgress, courseId])

    // Cleanup player on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current)
                progressIntervalRef.current = null
            }
            if (courseProgressIntervalRef.current) {
                clearInterval(courseProgressIntervalRef.current)
                courseProgressIntervalRef.current = null
            }
            if (playerRef.current) {
                try {
                    console.log('Disposing video player on unmount')
                    playerRef.current.dispose()
                } catch (err) {
                    console.error('Error disposing player on unmount:', err)
                }
                playerRef.current = null
            }
        }
    }, [])

    // Handle enrollment
    const handleEnroll = async () => {
        if (!courseId) return

        try {
            setEnrolling(true)
            const result = await createOrder({ BeURL, courseId })

            if (result.success) {
                alert('Enrollment request submitted! Waiting for admin approval.')
                // Refresh course data
                const refreshResult = await fetchCourseDetail({ BeURL, courseId })
                if (refreshResult.success) {
                    setEnrollmentStatus(refreshResult.enrollmentStatus)
                }
            } else {
                alert(result.message || 'Enrollment failed')
            }
        } catch (err) {
            console.error('Enrollment error:', err)
            alert('An error occurred during enrollment')
        } finally {
            setEnrolling(false)
        }
    }

    // Handle session selection
    const handleSessionClick = (session, syllabusId, isFirstVideo, isLocked) => {
        // Prevent selecting locked content
        if (isLocked) {
            alert('This content is locked. Please enroll in the course to access all lessons.')
            return
        }

        setSelectedSession({
            ...session,
            syllabusId
        })

        // Scroll to video player after a short delay to ensure content is rendered
        setTimeout(() => {
            if (videoRef.current) {
                videoRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                })
            }
        }, 100)
    }

    // Handle marking non-video content as completed
    const handleMarkComplete = async (sessionId, e) => {
        e.stopPropagation() // Prevent triggering session click

        if (!sessionId || !courseId) return

        try {
            const result = await markAsCompleted({ BeURL, sessionId, courseId })

            if (result.success) {
                // Update watchProgress immediately
                setWatchProgress(prev => ({
                    ...prev,
                    [sessionId]: { completed: true, time: 0 }
                }))

                // Refresh course progress
                const courseProgressResult = await getCourseProgress({ BeURL, courseId })
                if (courseProgressResult.success && courseProgressResult.progress) {
                    setCourseProgress(courseProgressResult.progress)
                }

                // Refresh individual session progress
                const progressResult = await getWatchProgress({ BeURL, courseId })
                if (progressResult.success) {
                    setWatchProgress(progressResult.progress || {})
                }
            }
        } catch (err) {
            console.error('Error marking as completed:', err)
        }
    }

    // Handle feedback submission
    const handleSubmitFeedback = async (e) => {
        e.preventDefault()

        if (feedbackData.rating < 1 || feedbackData.rating > 5) {
            alert('Please select a rating between 1 and 5')
            return
        }

        if (feedbackData.description.trim().length < 10) {
            alert('Please provide at least 10 characters in your feedback')
            return
        }

        setSubmittingFeedback(true)

        try {
            const result = await submitCourseFeedback({
                BeURL,
                courseId,
                rating: feedbackData.rating,
                description: feedbackData.description
            })

            if (result.success) {
                alert('Thank you for your feedback!')
                setHasFeedback(true)
                setShowFeedbackForm(false)
                setFeedbackData({ rating: 0, description: '' })
            } else {
                alert(result.message || 'Failed to submit feedback')
            }
        } catch (err) {
            console.error('Error submitting feedback:', err)
            alert('An error occurred while submitting feedback')
        } finally {
            setSubmittingFeedback(false)
        }
    }

    // Handle messaging instructor
    const handleMessageInstructor = async () => {
        if (!enrollmentStatus.hasAccess) {
            alert('Please enroll in this course to message the instructor')
            return
        }

        if (!course?._id || !course?.createdBy) {
            alert('Instructor information not available')
            return
        }

        setMessagingLoading(true)

        try {
            const result = await startConversation({
                BeURL,
                courseId: course._id,
                otherUserId: course.createdBy
            })

            if (result.success) {
                // Navigate to communications with conversation info
                setShowMessager(true)
            } else {
                alert(result.message || 'Failed to start conversation')
            }
        } catch (err) {
            console.error('Error starting conversation:', err)
            alert('An error occurred while starting conversation')
        } finally {
            setMessagingLoading(false)
        }
    }

    // Calculate discounted price
    const calculateDiscountedPrice = () => {
        if (!course) return 0
        const basePrice = course.price || 0
        const discount = course.discountPercent || 0
        return basePrice - (basePrice * discount / 100)
    }

    const handleMessager = () => {
        if(!showMessager){
            setShowMessager(true)
        }
        else(
            setShowMessager(false)
        )
    }

    // Calculate average rating
    const calculateAverageRating = () => {
        if (!course?.feedback || course.feedback.length === 0) {
            return { average: 0, count: 0 }
        }

        const totalRating = course.feedback.reduce((sum, fb) => sum + (fb.rating || 0), 0)
        const average = totalRating / course.feedback.length

        return {
            average: Math.round(average * 10) / 10, // Round to 1 decimal
            count: course.feedback.length
        }
    }

    if (loading) {
        return <Loading />
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <FiAlertCircle className="mx-auto text-red-500" size={48} />
                    <p className="mt-4 text-gray-600">{error}</p>
                    <button
                        onClick={() => navigate('/learner/courses')}
                        className="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        Back to Courses
                    </button>
                </div>
            </div>
        )
    }

    if (!course) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <p className="text-gray-600">Course not found</p>
            </div>
        )
    }


    const finalPrice = calculateDiscountedPrice()
    const { average: avgRating, count: reviewCount } = calculateAverageRating()

    return (
        <div className="min-h-screen bg-gray-50 space-y-10">
            {/* Header */}
            <div className="bg-white shadow-sm rounded-xl">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <button
                        onClick={() => navigate('/learner/courses')}
                        className="text-primary-600 hover:text-primary-700 mb-4 flex items-center gap-2"
                    >
                        ← Back to Courses
                    </button>
                    <h2 className="text-3xl font-bold text-gray-900">{course?.title}</h2>
                    <div className="mt-2">
                        <p
                            className={`text-gray-600 ${showFullDesc ? "" : "line-clamp-2"
                                }`}
                        >
                            {course?.shortDescription}
                        </p>

                        {course?.shortDescription?.length > 120 && (
                            <button
                                onClick={() => setShowFullDesc(!showFullDesc)}
                                className="text-primary-600 text-sm hover:underline mt-1"
                            >
                                {showFullDesc ? "See less" : "See more"}
                            </button>
                        )}
                    </div>

                    {/* Rating Display */}
                    {reviewCount > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                            <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <FiStar
                                        key={star}
                                        size={18}
                                        className={star <= Math.round(avgRating)
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'text-gray-300'}
                                    />
                                ))}
                            </div>
                            <span className="text-lg font-semibold text-gray-900">{avgRating}</span>
                            <span className="text-sm text-gray-500">({reviewCount} {reviewCount === 1 ? 'review' : 'reviews'})</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content - Video Player & Course Details */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Video Player or Preview */}
                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            {selectedSession && selectedSession.type === 'video' ? (
                                <div className="relative">
                                    <video
                                        ref={videoRef}
                                        className="video-js vjs-big-play-centered w-full"
                                        style={{ minHeight: '400px' }}
                                    />
                                    {/* Security Watermark */}
                                    {enrollmentStatus.hasAccess && currentUser && (
                                        <div
                                            className="absolute pointer-events-none"
                                            style={{
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                opacity: 0.15,
                                                fontSize: '14px',
                                                color: '#ffffff',
                                                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                                userSelect: 'none',
                                                zIndex: 1
                                            }}
                                        >
                                            {currentUser.email || currentUser.studentCode || 'Licensed Content'}
                                        </div>
                                    )}
                                    {!enrollmentStatus.hasAccess && (
                                        <div className="absolute top-4 right-4 bg-yellow-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2">
                                            <FiLock size={16} />
                                            <span className="text-sm font-semibold">Preview Mode (5 min)</span>
                                        </div>
                                    )}
                                    {/* Video Error Overlay */}
                                    {videoError && (
                                        <div className="absolute inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-10">
                                            <div className="bg-white rounded-lg p-6 max-w-md mx-4 text-center shadow-xl">
                                                <div className="text-red-500 mb-4">
                                                    <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Video Player Error</h3>
                                                <p className="text-gray-600 mb-6">
                                                    The video player encountered an error. This can happen after the page has been idle for a while.
                                                </p>
                                                <button
                                                    onClick={() => {
                                                        setVideoError(false)
                                                        // Force player reinitialization by toggling a dependency
                                                        setSelectedSession({ ...selectedSession })
                                                    }}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 flex items-center justify-center gap-2 mx-auto"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                    </svg>
                                                    Reload Video Player
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : selectedSession && selectedSession.type === 'pdf' ? (
                                <div className="p-8 text-center">
                                    <p className="text-gray-600">PDF Content</p>
                                    {enrollmentStatus.hasAccess && selectedSession.file?.url && (
                                        <a
                                            href={`${BeURL}${selectedSession.file.url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-4 inline-block px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                                        >
                                            Open PDF
                                        </a>
                                    )}
                                    {!enrollmentStatus.hasAccess && (
                                        <p className="mt-4 text-yellow-600">Enroll to access this content</p>
                                    )}
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    <FiPlay size={48} className="mx-auto mb-4" />
                                    <p>Select a lesson to start watching</p>
                                </div>
                            )}
                        </div>

                        {/* Course Progress - Show only if enrolled with access */}
                        {enrollmentStatus.isEnrolled && enrollmentStatus.hasAccess && (
                            <div className="bg-white rounded-xl shadow-lg p-6">
                                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-sm font-semibold text-gray-800">Course Progress</h3>
                                        <span className="text-2xl font-bold text-indigo-600">
                                            {courseProgress.percentage}%
                                        </span>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2 overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-500 ease-out"
                                            style={{ width: `${courseProgress.percentage}%` }}
                                        />
                                    </div>

                                    <div className="text-xs text-gray-600">
                                        <span className="font-medium">
                                            {courseProgress.completedAssets || 0} of {courseProgress.totalAssets || 0} lessons completed
                                        </span>
                                    </div>

                                    {courseProgress.percentage === 100 && (
                                        <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-lg flex items-center gap-2">
                                            <FiCheckCircle className="text-green-600" size={18} />
                                            <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
                                                Congratulations! Course completed <LuPartyPopper className="text-yellow-500" size={18} />
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Feedback Form - Show when progress > 75% and not submitted yet */}
                        {enrollmentStatus.isEnrolled && enrollmentStatus.hasAccess &&
                            courseProgress.percentage >= 75 && !hasFeedback && (
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-orange-200 rounded-lg">
                                        {!showFeedbackForm ? (
                                            <div>
                                                <div className="flex items-center gap-2 mb-2">
                                                    <FiStar className="text-orange-500" size={20} />
                                                    <h3 className="text-sm font-semibold text-gray-800">Share Your Feedback</h3>
                                                </div>
                                                <p className="text-xs text-gray-600 mb-3">
                                                    You've completed {courseProgress.percentage}% of this course! Help others by sharing your experience.
                                                </p>
                                                <button
                                                    onClick={() => setShowFeedbackForm(true)}
                                                    className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium text-sm"
                                                >
                                                    Write a Review
                                                </button>
                                            </div>
                                        ) : (
                                            <form onSubmit={handleSubmitFeedback}>
                                                <h3 className="text-sm font-semibold text-gray-800 mb-3">Write Your Review</h3>

                                                {/* Star Rating */}
                                                <div className="mb-4">
                                                    <label className="text-xs text-gray-700 font-medium mb-2 block">
                                                        Rating *
                                                    </label>
                                                    <div className="flex gap-2">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <button
                                                                key={star}
                                                                type="button"
                                                                onClick={() => setFeedbackData(prev => ({ ...prev, rating: star }))}
                                                                className="transition-colors"
                                                            >
                                                                <FiStar
                                                                    size={28}
                                                                    className={star <= feedbackData.rating
                                                                        ? 'fill-yellow-400 text-yellow-400'
                                                                        : 'text-gray-300'}
                                                                />
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Feedback Text */}
                                                <div className="mb-4">
                                                    <label className="text-xs text-gray-700 font-medium mb-2 block">
                                                        Your Feedback * (min 10 characters)
                                                    </label>
                                                    <textarea
                                                        value={feedbackData.description}
                                                        onChange={(e) => setFeedbackData(prev => ({ ...prev, description: e.target.value }))}
                                                        placeholder="Share your experience with this course..."
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                                        rows="4"
                                                        required
                                                    />
                                                </div>

                                                {/* Action Buttons */}
                                                <div className="flex gap-2">
                                                    <button
                                                        type="submit"
                                                        disabled={submittingFeedback}
                                                        className="flex-1 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium text-sm disabled:bg-gray-400"
                                                    >
                                                        {submittingFeedback ? 'Submitting...' : 'Submit Review'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setShowFeedbackForm(false)
                                                            setFeedbackData({ rating: 0, description: '' })
                                                        }}
                                                        className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium text-sm"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            )}

                        {/* About This Course */}
                        <div className="bg-white rounded-xl shadow-lg p-6 space-y-6">
                            <h2 className="font-semibold mb-4 flex items-center gap-2">About This Course</h2>
                            <div className="mb-6">
                                <p
                                    className={`text-gray-700 whitespace-pre-wrap transition-all duration-300 ${showFullDesc ? "" : "line-clamp-4"
                                        }`}
                                >
                                    {course.description}
                                </p>

                                {course?.description?.length > 200 && (
                                    <button
                                        onClick={() => setShowFullDesc(!showFullDesc)}
                                        className="text-primary-600 hover:underline text-sm font-medium mt-2"
                                    >
                                        {showFullDesc ? "See less" : "See more"}
                                    </button>
                                )}
                            </div>

                            <div>
                                {course.skills && course.skills.length > 0 && (
                                    <div className="mt-6">
                                        <h3 className="font-semibold mb-4 flex items-center gap-2">
                                            Skills what you learn
                                        </h3>

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {course.skills.map((skill, idx) => (
                                                <div key={idx} className="flex items-center gap-2 text-gray-700 text-sm">
                                                    <FiCheckCircle className="text-green-500 flex-shrink-0" size={16} />
                                                    <span className=' capitalize'>{skill}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>



                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiBook className="text-primary-600" size={20} />
                                        <p className="text-sm text-gray-600">Level</p>
                                    </div>
                                    <p className="font-semibold capitalize">{course.level}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiGlobe className="text-primary-600" size={20} />
                                        <p className="text-sm text-gray-600">Language</p>
                                    </div>
                                    <p className="font-semibold">{course.language}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiUsers className="text-primary-600" size={20} />
                                        <p className="text-sm text-gray-600">Department</p>
                                    </div>
                                    <p className="font-semibold">{course.department?.name || 'General'}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FiClock className="text-primary-600" size={20} />
                                        <p className="text-sm text-gray-600">Lessons</p>
                                    </div>
                                    <p className="font-semibold">
                                        {course.syllabuses?.reduce((acc, s) => acc + (s.assets?.length || 0), 0) || 0}
                                    </p>
                                </div>
                            </div>

                            <div>
                                {course.faq && course.faq.length > 0 && (
                                    <div className="mt-12">
                                        <h2 className="font-semibold mb-4 flex items-center gap-2">
                                            Course FAQs
                                        </h2>
                                        <div className="divide-y divide-gray-200">
                                            {course.faq.map((faq, index) => {
                                                const isOpen = openFAQ === index
                                                return (
                                                    <div key={index} className="py-2">

                                                        {/* Question */}
                                                        <button
                                                            onClick={() => setOpenFAQ(isOpen ? null : index)}
                                                            className="w-full flex items-center justify-between text-left"
                                                        >
                                                            <span className="">
                                                                {faq.question}
                                                            </span>

                                                            <span
                                                                className={`text-gray-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""
                                                                    }`}
                                                            >
                                                                <MdKeyboardArrowDown />
                                                            </span>
                                                        </button>

                                                        {/* Answer */}
                                                        <div
                                                            className={`transition-all duration-300 overflow-hidden ${isOpen ? "max-h-40 mt-2" : "max-h-0"
                                                                }`}
                                                        >
                                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                                {faq.answer}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div>
                                <FeedbackSection course={course} />
                            </div>
                        </div>
                    </div>

                    {/* Sidebar - Price, Enrollment & Course Content */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* Price & Enrollment */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-bold mb-4">Course Pricing</h3>

                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-2">
                                    <BiRupee className="text-2xl" />
                                    <span className="text-3xl font-bold">{finalPrice.toFixed(2)}</span>
                                    {course.discountPercent > 0 && (
                                        <span className="text-gray-500 line-through text-lg ml-2">
                                            <BiRupee className="inline" />
                                            {course.price}
                                        </span>
                                    )}
                                </div>
                                {course.discountPercent > 0 && (
                                    <p className="text-green-600 text-sm">{course.discountPercent}% off</p>
                                )}
                            </div>

                            {/* Enrollment Status */}
                            {enrollmentStatus.isEnrolled ? (
                                <div className="space-y-4">
                                    {enrollmentStatus.hasAccess ? (
                                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-green-700">
                                                <FiCheckCircle size={20} />
                                                <span className="font-semibold">You're enrolled!</span>
                                            </div>
                                            <p className="text-sm text-green-600 mt-1">
                                                Full access granted
                                            </p>
                                        </div>
                                    ) : enrollmentStatus.accessExpired ? (
                                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-red-700">
                                                <FiAlertCircle size={20} />
                                                <span className="font-semibold">Access Expired</span>
                                            </div>
                                            <p className="text-sm text-red-600 mt-1">
                                                Your access to this course has expired
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <div className="flex items-center gap-2 text-yellow-700">
                                                <FiClock size={20} />
                                                <span className="font-semibold">Pending Approval</span>
                                            </div>
                                            <p className="text-sm text-yellow-600 mt-1">
                                                Waiting for admin approval to access full content
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <button
                                    onClick={handleEnroll}
                                    disabled={enrolling}
                                    className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-400 font-semibold transition-colors"
                                >
                                    {enrolling ? 'Enrolling...' : 'Enroll Now'}
                                </button>
                            )}

                            {/* Preview Notice */}
                            {!enrollmentStatus.hasAccess && (
                                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm text-blue-700 font-semibold mb-1">
                                        <FiAlertCircle className="inline mr-1" />
                                        Preview Available
                                    </p>
                                    <p className="text-xs text-blue-600">
                                        • Watch the first video (up to 5 minutes)<br />
                                        • Other content is locked until enrollment
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Course Content */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-xl font-bold mb-4">Course Content</h2>
                            <div className="space-y-2">
                                {course.syllabuses && course.syllabuses.length > 0 ? (
                                    course.syllabuses.map((syllabus, idx) => (
                                        <div key={syllabus.id || idx} className="border rounded-lg">
                                            <button
                                                onClick={() => setExpandedSyllabus(expandedSyllabus === syllabus.id ? null : syllabus.id)}
                                                className="w-full flex items-center justify-between p-4 hover:bg-gray-50"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="w-8 h-8 flex items-center justify-center bg-primary-100 text-primary-700 rounded-full font-semibold">
                                                        {idx + 1}
                                                    </span>
                                                    <span className="font-semibold text-left">{syllabus.name}</span>
                                                    <span className="text-sm text-gray-500">
                                                        ({syllabus.assets?.length || 0} lessons)
                                                    </span>
                                                </div>
                                                {expandedSyllabus === syllabus.id ? <FiChevronUp /> : <FiChevronDown />}
                                            </button>

                                            {expandedSyllabus === syllabus.id && (
                                                <div className="border-t">
                                                    {syllabus.assets && syllabus.assets.length > 0 ? (
                                                        syllabus.assets.map((asset, assetIdx) => {
                                                            const isSelected = selectedSession?._id === asset._id
                                                            const progress = watchProgress[asset._id]
                                                            const isCompleted = progress?.completed

                                                            // Get session progress from courseProgress if available
                                                            const sessionProgressData = courseProgress.sessionProgress?.[asset._id]
                                                            const sessionPercentage = sessionProgressData?.percentage || 0

                                                            // Check if this is the first video in the entire course
                                                            const syllabusIndex = course.syllabuses?.findIndex(s => s.id === syllabus.id) || 0
                                                            const isFirstVideo = syllabusIndex === 0 && assetIdx === 0 && asset.type === 'video'

                                                            // Determine if content is locked
                                                            const isLocked = !enrollmentStatus.hasAccess && (!isFirstVideo || asset.type !== 'video')

                                                            // Determine progress color
                                                            const getProgressColor = (pct) => {
                                                                if (pct === 100) return 'text-green-600 font-semibold'
                                                                if (pct >= 90) return 'text-green-500'
                                                                if (pct >= 50) return 'text-yellow-600'
                                                                return 'text-red-500'
                                                            }

                                                            return (
                                                                <div
                                                                    key={asset._id || assetIdx}
                                                                    className={`flex items-center justify-between p-4 border-b last:border-b-0 ${isLocked ? 'cursor-not-allowed opacity-60' : 'hover:bg-gray-50'} ${isSelected ? 'bg-primary-50' : ''
                                                                        }`}
                                                                >
                                                                    <button
                                                                        onClick={() => handleSessionClick(asset, syllabus.id, isFirstVideo, isLocked)}
                                                                        disabled={isLocked}
                                                                        className="flex-1 flex items-center gap-3 text-left"
                                                                    >
                                                                        {asset.type === 'video' ? (
                                                                            <FiPlay className={isSelected ? 'text-primary-600' : 'text-gray-400'} />
                                                                        ) : (
                                                                            <FiBook className={isSelected ? 'text-primary-600' : 'text-gray-400'} />
                                                                        )}
                                                            
                                                                            <span className={`${isSelected ? 'text-primary-600 font-semibold' : ''}`}>
                                                                                {asset.title}
                                                                            </span>
                                                                
                                                                        {!enrollmentStatus.hasAccess && isFirstVideo && (
                                                                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                                                                                Preview
                                                                            </span>
                                                                        )}
                                                                        {isLocked && (
                                                                            <FiLock className="text-gray-400" size={14} />
                                                                        )}
                                                                    </button>



                                                                    <div className="flex items-center gap-2 ml-auto">
                                                                        {/* Show percentage or Mark Complete button */}
                                                                        {enrollmentStatus.hasAccess && (
                                                                            <>
                                                                                {isCompleted ? (
                                                                                    <span className={`text-sm font-medium ${getProgressColor(100)}`}>
                                                                                        100%
                                                                                    </span>
                                                                                ) : asset.type !== 'video' ? (
                                                                                    <button
                                                                                        onClick={(e) => handleMarkComplete(asset._id, e)}
                                                                                        className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                                                                                        title="Mark as completed"
                                                                                    >
                                                                                        Complete
                                                                                    </button>
                                                                                ) : sessionPercentage > 0 ? (
                                                                                    <span className={`text-sm font-medium ${getProgressColor(sessionPercentage)}`}>
                                                                                        {sessionPercentage}%
                                                                                    </span>
                                                                                ) : null}
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <p>
                                                                        {asset?.type === "video" && (
                                                                            <VideoDuration url={`${BeURL}${asset?.file?.url}`} />
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            )
                                                        })
                                                    ) : (
                                                        <p className="p-4 text-gray-500 text-sm">No lessons available</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <p className="text-gray-500">No syllabus available</p>
                                )}
                            </div>
                        </div>

                        {/* Instructor Section */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h3 className="text-lg font-bold mb-4">Instructor</h3>

                            {course.instructor ? (
                                <>
                                    <div className="flex items-center gap-4 mb-4">
                              
                                        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-600 to-secondary-500 flex items-center justify-center flex-shrink-0">
                                            <span className="text-white font-bold text-xl">
                                                {(course.instructor.name || course.instructor.firstname || 'I')[0].toUpperCase()}
                                            </span>
                                        </div>

                            
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900">
                                                {course.instructor.name || `${course.instructor.firstname || ''} ${course.instructor.lastname || ''}`.trim() || 'Instructor'}
                                            </h4>
                                            <p className="text-sm text-gray-600">Course Instructor</p>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-600 to-secondary-500 flex items-center justify-center flex-shrink-0">
                                        <span className="text-white font-bold text-xl">I</span>
                                    </div>
                                    <div className="flex-1">
                                        <h4 className="font-semibold text-gray-900">Course Instructor</h4>
                                        <p className="text-sm text-gray-600">Learning Guide</p>
                                    </div>
                                </div>
                            )}

                            {enrollmentStatus.hasAccess && (
                                <button
                                    onClick={handleMessageInstructor}
                                    disabled={messagingLoading}
                                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${messagingLoading
                                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                        : 'bg-primary-600 text-white hover:bg-primary-700 active:scale-95'
                                        }`}
                                >
                                    <FiMessageCircle size={18} />
                                    {messagingLoading ? 'Loading...' : 'Message Instructor'}
                                </button>
                            )}
                            {!enrollmentStatus.hasAccess && (
                                <p className="text-xs text-gray-500 text-center py-3">
                                    Enroll in the course to message the instructor
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {/*{Floating icon}*/}
            <div className="fixed bottom-6 right-6 z-50">
                <button
                    onClick={handleMessager}
                    className="flex items-center justify-center w-14 h-14 rounded-full 
                   bg-gradient-to-r from-primary-600 to-secondary-600 
                   text-white shadow-lg hover:scale-110 
                   transition-all duration-300"
                >
                    <TbMessage size={26} />
                </button>
            </div>
            {showMessager && (
                <div  className="fixed bottom-24 right-10  rounded-tl-xl rounded-tr-xl z-50 flex flex-col">
                    <FetchMessages course={course} setShowMessager={setShowMessager} />
                </div>
            )}
        </div>
    )
}

export default CourseView